import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../../hooks/useAppDispatch";
import { RootState } from "../../../redux/store";
import { Input, Button } from "@repo/ui";
import LocationPicker from "../../../components/LocationPicker/LocationPicker";
import { updateGiftTable } from "../../../redux/slices/giftTableSlice";
import { useNavigate } from "react-router-dom";
import editIcon from "../../../assets/icons/edit.svg";
import placeHolderImage from "../../../assets/placeholders/picture.jpg";
import {
  generateGetURL,
  generateUploadURL,
  uploadMedia,
  verifyMediaUpload,
} from "../../../services/mediaApiService";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import { eventTypeLocale } from "../../../constants/eventTypeLocale";
import { themeLocale } from "../../../constants/themeLocale";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_components_EditTableContent";

const EditTableContent: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const giftTable = useAppSelector((state: RootState) => state.giftTable);
  const userId = useAppSelector((state: RootState) => state.auth.user?.id);

  const [eventName, setEventName] = useState(giftTable.eventName);
  const [eventPlace, setEventPlace] = useState(giftTable.eventPlace);
  const [eventLocation, setEventLocation] = useState(giftTable.eventLocation);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (giftTable.profilePicture) {
        try {
          const media = await generateGetURL(giftTable.profilePicture);
          setProfileImageUrl(media.signedURL);
        } catch (error) {
          console.error(t("errorFetchingImage"), error);
          setProfileImageUrl(null);
        }
      }
    };

    fetchProfileImage();
  }, [giftTable.profilePicture]);

  useEffect(() => {
    if (giftTable.eventLocation) setEventLocation(giftTable.eventLocation);
    if (giftTable.eventPlace) setEventPlace(giftTable.eventPlace);
    if (giftTable.eventName) setEventName(giftTable.eventName);
  }, [giftTable]);

  const handleSaveChanges = () => {
    dispatch(updateGiftTable({ eventName, eventPlace, eventLocation }));
    navigate("/my-table");
  };

  const handleEditImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files[0]) return;

    const file = event.target.files[0];
    const fileName = `${new Date().toISOString().replace(/[:.-]/g, "")}-${userId}-gift-table-profile.${file.name.split(".").pop()}`;
    setLoading(true);

    try {
      const { presignedURL, mediaID } = await generateUploadURL(
        fileName,
        userId!
      );
      await uploadMedia(presignedURL, file);
      await verifyMediaUpload(mediaID);
      dispatch(updateGiftTable({ profilePicture: mediaID }));
      const media = await generateGetURL(mediaID);
      setProfileImageUrl(media.signedURL);
    } catch (error) {
      console.error(t("errorUploadingImage"), error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-8 max-w-4xl">
      {/* Profile Picture */}
      <div className="relative flex justify-center">
        <div className="relative">
          {loading ? (
            <div className="w-64 h-64 rounded-full flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <img
              src={profileImageUrl || placeHolderImage}
              alt={t("profileImage")}
              className="w-64 h-64 rounded-full object-cover mb-4"
            />
          )}
          <div className="absolute top-2 right-2 p-1 bg-white rounded-full cursor-pointer">
            <label htmlFor="uploadImage" className="cursor-pointer">
              <img src={editIcon} alt={t("editImage")} className="h-5 w-5" />
            </label>
            <input
              id="uploadImage"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleEditImage}
            />
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-sm text-gray-600">{t("editWarning")}</p>

      {/* Event Name */}
      <div>
        <h2 className="text-lg font-bold mb-2">{t("eventNameLabel")}</h2>
        <Input
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          placeholder={t("eventNamePlaceholder")}
        />
      </div>

      {/* Event Place */}
      <div>
        <h2 className="text-lg font-bold mb-2">{t("eventPlaceLabel")}</h2>
        <Input
          value={eventPlace}
          onChange={(e) => setEventPlace(e.target.value)}
          placeholder={t("eventPlacePlaceholder")}
        />
      </div>

      {/* Event Location */}
      <div>
        <h2 className="text-lg font-bold mb-2">{t("eventLocationLabel")}</h2>
        <LocationPicker
          value={eventLocation.address}
          onChange={(location) => setEventLocation(location)}
        />
      </div>

      {/* Non-editable Fields */}
      <div className="mt-8 space-y-4">
        <p className="text-sm text-gray-600">{t("nonEditableFields")}</p>
        <p className="text-sm text-gray-600">
          <strong>{t("eventType")}:</strong>{" "}
          {t(eventTypeLocale[giftTable.eventType]) || "N/A"}
        </p>
        <p className="text-sm text-gray-600">
          <strong>{t("eventTheme")}:</strong>{" "}
          {t(themeLocale[giftTable.theme]) || "N/A"}
        </p>
        <p className="text-sm text-gray-600">
          <strong>{t("eventDate")}:</strong> {giftTable.eventDate || "N/A"}
        </p>
      </div>

      <Button
        label={t("saveButton")}
        type="primary"
        onClick={handleSaveChanges}
        className="w-full mt-8"
      />
    </div>
  );
};

export default withTranslations(LOCALE)(EditTableContent);
